import React, { useEffect, useRef, useState } from "react";
import { BeforeAfter as BeforeAfterApi } from "@/lib/api";
import EditableHTML from "./EditableHTML";

const BeforeAfterSlider = ({ before, after, label }) => {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const setFromX = (clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const ratio = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, ratio)));
  };

  return (
    <div
      ref={containerRef}
      className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10 select-none cursor-ew-resize touch-none"
      onMouseDown={(e) => { dragging.current = true; setFromX(e.clientX); }}
      onMouseMove={(e) => { if (dragging.current) setFromX(e.clientX); }}
      onMouseUp={() => (dragging.current = false)}
      onMouseLeave={() => (dragging.current = false)}
      onTouchStart={(e) => { dragging.current = true; setFromX(e.touches[0].clientX); }}
      onTouchMove={(e) => { if (dragging.current) setFromX(e.touches[0].clientX); }}
      onTouchEnd={() => (dragging.current = false)}
      data-testid="ba-slider"
    >
      <img src={after} alt="after" className="absolute inset-0 w-full h-full object-cover pointer-events-none" draggable={false} />
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ width: `${pos}%` }}>
        <img src={before} alt="before" className="absolute inset-0 h-full object-cover" style={{ width: `${100 / (pos / 100)}%`, maxWidth: "none" }} draggable={false} />
      </div>
      <div
        className="absolute top-0 bottom-0 w-px bg-white shadow-[0_0_18px_rgba(30,144,255,0.7)] pointer-events-none"
        style={{ left: `${pos}%` }}
      />
      <div
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-10 w-10 rounded-full brand-gradient flex items-center justify-center text-white text-xs font-bold pointer-events-none"
        style={{ left: `${pos}%` }}
      >
        ⇆
      </div>
      <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-rose-500/80 text-white text-[10px] uppercase tracking-wider font-semibold">Before</div>
      <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-atc-green/90 text-slate-950 text-[10px] uppercase tracking-wider font-semibold">After</div>
      {label && (
        <div className="absolute bottom-3 left-3 right-3 text-white text-xs sm:text-sm font-medium bg-slate-950/60 backdrop-blur px-3 py-1.5 rounded-md pointer-events-none">
          {label}
        </div>
      )}
    </div>
  );
};

const GallerySection = ({ content = {} }) => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    (async () => {
      try { setItems(await BeforeAfterApi.list(true)); } catch {}
    })();
  }, []);

  // Hidden by explicit CMS toggle
  if (content.show_gallery === "false" || content.show_gallery === false) return null;
  if (items.length === 0) return null;

  return (
    <section className="water-section py-28" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-14">
          <div className="text-xs uppercase tracking-[0.22em] text-atc-blue mb-5">
            <EditableHTML html={content.gallery_eyebrow} as="span" />
          </div>
          <EditableHTML
            html={content.gallery_title}
            className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-white"
          />
          <EditableHTML
            html={content.gallery_subtitle}
            className="text-slate-400 mt-4 max-w-xl"
          />
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((b) => (
            <div key={b.id} className="space-y-4" data-testid={`ba-item-${b.id}`}>
              <BeforeAfterSlider before={b.before_url} after={b.after_url} label={b.title} />
              {b.description && (
                <p className="text-sm text-slate-400 leading-relaxed">{b.description}</p>
              )}
            </div>
          ))}
        </div>
        <div className="text-xs text-slate-500 mt-6">Drag the slider on any image to reveal the before / after.</div>
      </div>
    </section>
  );
};

export default GallerySection;
